import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { ShieldQuestion, Loader2, AlertTriangle } from "lucide-react";

const COLOR = {
  pdp_ecrit_obligatoire: "#B3382C", pdp_volontaire: "#136B79", protocole_securite: "#E49B1A",
  coordination_sps_a_analyser: "#E49B1A", donnees_insuffisantes: "#5C6E70",
  hors_champ_interference: "#5C6E70", regime_a_confirmer: "#5C6E70",
};

// Carte évolutive : régime pressenti + confiance + informations restantes. Recalcul auto (stateless).
export default function QualificationPreview({ form }) {
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const inputs = {
    schemaVersion: 1,
    entrepriseExterieurePresente: form.entrepriseExterieure,
    operationChargementDechargement: form.qualChargement ?? null,
    chantierBatimentGenieCivil: form.qualChantierBTP ?? null,
    plusieursEntreprises: form.qualPlusieurs ?? null,
    volumeHorairePrevisionnel: (form.qualVolume === "" || form.qualVolume == null) ? null : Number(form.qualVolume),
    siteEnActivite: form.siteOccupe,
    sousTraitance: form.sousTraitance,
    risquesInterference: null,
    periodeMois: null, effectifPrevisionnel: null, natureOperation: null, commentairesQualification: null,
  };
  const body = {
    inputs, natureTravaux: form.natureTravaux, entrepriseExterieure: form.entrepriseExterieure,
    siteOccupe: form.siteOccupe, sousTraitance: form.sousTraitance,
    presencePublicOuCollaborateurs: form.presencePublicOuCollaborateurs, zones: form.zones,
  };
  const dep = JSON.stringify(body);

  useEffect(() => {
    setLoading(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const r = await api.post("/qualification/simuler", JSON.parse(dep));
        setQ(r.data.qualification);
      } catch (e) { /* silencieux pendant la saisie */ }
      finally { setLoading(false); }
    }, 450);
    return () => clearTimeout(timer.current);
  }, [dep]);

  const color = q ? (COLOR[q.regime] || "#5C6E70") : "#5C6E70";
  const pct = q?.confiancePct ?? 0;

  return (
    <div className="card" data-testid="qualification-preview" style={{ borderLeft: `4px solid ${color}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <ShieldQuestion size={17} color={color} />
        <h3 style={{ fontSize: ".95rem", margin: 0 }}>Qualification réglementaire</h3>
        {loading && <Loader2 size={14} className="spin" style={{ animation: "spin 1s linear infinite" }} />}
      </div>

      {!q ? (
        <p style={{ fontSize: ".82rem", color: "var(--text-muted)", marginTop: 8 }} data-testid="qual-preview-status">Qualification en cours…</p>
      ) : (
        <>
          <div style={{ margin: "10px 0 6px", fontSize: ".72rem", textTransform: "uppercase", letterSpacing: ".04em", color: "var(--text-muted)" }}>Régime pressenti</div>
          <div data-testid="qual-preview-regime" style={{ display: "inline-block", background: color, color: "#fff", padding: "4px 12px", borderRadius: 999, fontSize: ".82rem", fontWeight: 700 }}>{q.regimeLabel}</div>

          <div style={{ marginTop: 12, fontSize: ".72rem", textTransform: "uppercase", letterSpacing: ".04em", color: "var(--text-muted)" }}>Confiance</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1, height: 8, background: "var(--ligne)", borderRadius: 6, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${pct}%`, background: color, transition: "width .3s" }} />
            </div>
            <b data-testid="qual-preview-confiance" style={{ fontSize: ".82rem", color }}>{pct}%</b>
          </div>

          {q.validationHumaineRequise && (
            <div className="notice" style={{ background: "#FDF3E0", borderColor: "#E49B1A", marginTop: 10, fontSize: ".8rem" }}>
              <AlertTriangle size={12} style={{ verticalAlign: -2 }} /> Une validation humaine (HSE / juridique) sera requise pour ce régime.
            </div>
          )}

          {(q.informationsManquantes || []).length > 0 ? (
            <div style={{ marginTop: 12 }} data-testid="qual-preview-manquants">
              <div style={{ fontSize: ".72rem", textTransform: "uppercase", letterSpacing: ".04em", color: "var(--text-muted)", marginBottom: 4 }}>Informations encore nécessaires</div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: ".82rem", lineHeight: 1.8 }}>
                {q.informationsManquantes.map((m, i) => <li key={i}>☐ {m.label}</li>)}
              </ul>
            </div>
          ) : (
            <p style={{ fontSize: ".8rem", color: "var(--vert, #2E7D5B)", marginTop: 10 }}>✓ Informations suffisantes pour ce régime.</p>
          )}

          <p style={{ fontSize: ".76rem", color: "var(--text-muted)", marginTop: 10 }}>
            La qualification évolue au fil de la saisie et sera recalculée à chaque évolution de l'opération.
          </p>
        </>
      )}
    </div>
  );
}
